/**
 * Field onboarding — the launch stand, in one screen.
 *
 *   "I'm going to hire field staff to help onboard as many service profile
 *    users and providers as possible at launch, and I want a way to track all
 *    the profiles that THEY assisted to create profiles and login in order to
 *    properly reward and compensate them."
 *
 * BUILT FOR A PERSON STANDING UP, holding a tablet, with somebody waiting. The
 * whole screen is one field and one button, the field keeps focus after a
 * submit, and the answer appears above it without moving anything. An agent
 * should be able to log four people in a minute without looking away.
 *
 * WHAT IT DOES NOT SHOW, AND WHY THAT IS THE FEATURE
 *
 * Nothing about the person behind the code. Not after a success, not in the
 * tally, not anywhere. The database refuses to return it — fn_log_my_field_
 * onboarding answers with a verdict and a sentence — and this screen does not
 * go looking elsewhere. A confirmation reading "Recorded — Thandi Mokoena" is
 * how a compensation tool becomes a member directory keyed to a staff member,
 * which is exactly what prohibition 6 forbids.
 *
 * The agent knows who they just helped. They are standing next to them.
 *
 * TWO PATHS, AND THE AGENT PICKS BEFORE THEY TYPE
 *
 *   "for them to GUIDE those who register on their own phones ... and for
 *    those who are assisted on the staff's side, it'll have to be something
 *    that's done from within the Portal itself."
 *
 * The choice is a pair of buttons at the top rather than one clever field that
 * sniffs whether an @ was typed. A code and an address do different things --
 * one claims an identity that exists, the other sends a stranger an email --
 * and a screen that guesses between them will one day guess wrong while
 * somebody is standing there waiting.
 *
 * THE ASSISTED PATH REPORTS THE SAME SENTENCE EVERY TIME, and that is the
 * database's doing rather than this screen's: a Portal that said "that address
 * is already a member" would be an "is this person on Orchid?" lookup handed
 * to every agent hired at launch.
 *
 * TWO HALVES, AND THE SECOND ONE IS THE DATABASE'S DECISION
 *
 * The ledger renders only when fn_field_onboarding_ledger answers. An agent
 * holding log_field_onboarding but not view_field_onboarding_ledger is refused,
 * and that refusal is the correct and expected answer for most people opening
 * this screen — so it draws nothing rather than an error. Asking the server
 * what to render is this portal's rule, and it is a better rule than reading a
 * permission list on the client, which can drift.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  O, SANS, DISPLAY, Card, Field, Notice, PrimaryCTA, Spinner, StatusPill, textInputStyle,
} from '../ui';
import {
  logFieldOnboarding, fetchMyOnboardingTally, fetchOnboardingLedger,
  fetchOnboardingPayLedger, randsFromCents,
  inviteAMemberIOnboarded, fetchMyInvitations,
  connectCodeProblem, tidyConnectCode, emailProblem, ROOMS,
} from '../lib/fieldOnboarding';

const Figure = ({ value, label, tone = O.ink }) => (
  <div style={{ minWidth: 74 }}>
    <p style={{
      fontFamily: DISPLAY, fontSize: 24, fontWeight: 700, color: tone,
      margin: 0, fontVariantNumeric: 'tabular-nums',
    }}>{value}</p>
    <p style={{ fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, margin: '2px 0 0' }}>
      {label}
    </p>
  </div>
);

/**
 * The agent's own numbers.
 *
 * "Awaiting" is not a failure and must not read as one. It is a person who was
 * logged and has not opened Orchid since, which is ordinary on the day of a
 * launch — most of them sign in that evening. Showing it in the same weight as
 * the others, with a sentence rather than a warning colour, keeps it a fact
 * instead of a reprimand.
 */
function MyTally({ tally, invitations, busy }) {
  if (busy && !tally) {
    return <div style={{ padding: 12 }}><Spinner /></div>;
  }
  const logged = tally?.logged ?? 0;
  const counted = tally?.counted ?? 0;
  const awaiting = tally?.awaiting ?? 0;
  const sent = invitations?.sent ?? 0;
  const openInvites = invitations?.waiting ?? 0;

  return (
    <div>
      {/* A GRID RATHER THAN A WRAPPING FLEX ROW. Four figures with a 26px gap
          wrapped into a ragged 2x2 on a phone, the second row starting under
          whichever figure happened to be widest. auto-fit gives four across on a
          desk, two on a phone, and columns that line up in both. */}
      <div style={{
        display: 'grid', gap: '14px 12px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))',
      }}>
        <Figure value={logged} label="logged" />
        <Figure value={counted} label="counted" tone={O.green} />
        <Figure value={awaiting} label="waiting on them" />
        {/* INVITATIONS SIT NEXT TO THE REST rather than in a card of their own.
            An agent switches between the two paths all day and cares about one
            number -- how many people they got onto Orchid -- so splitting the
            score in two would make them add it up themselves. A REDEEMED
            invitation has already become a logged onboarding, which is why
            only the open ones are shown here: counting both would count the
            same person twice. */}
        {sent > 0 && <Figure value={openInvites} label="invites open" />}
      </div>
      <p style={{
        fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, lineHeight: 1.65, margin: '12px 0 0',
      }}>
        {logged === 0
          ? 'Nothing logged yet. Log somebody’s OFX Code within 12 hours of them signing up, '
            + 'and it counts once they next open Orchid.'
          : `${awaiting === 0
              ? 'Everyone you have logged has been back into Orchid since.'
              : `${awaiting} of these have not opened Orchid since you logged them. `
                + 'They count as soon as they do — most people sign in the same evening.'}`}
      </p>
      {/* SAID SEPARATELY, because an open invitation is a different kind of
          waiting. The one above is somebody who has an Orchid account and has
          not come back; this is somebody who has not got one yet. Folding them
          into one sentence would make a person who never opened their email
          look like a person who signed up and went quiet. */}
      {openInvites > 0 && (
        <p style={{
          fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, lineHeight: 1.65, margin: '6px 0 0',
        }}>
          {`${openInvites} ${openInvites === 1 ? 'invitation is' : 'invitations are'} still open. `}
          They join the count above once the person chooses a password.
        </p>
      )}
    </div>
  );
}

/**
 * Human Capital's view. One row per agent, never per person.
 *
 * A per-person export would be a member list with a staff name attached, and
 * that is a different object with a different permission — Portal P6's shape,
 * built when somebody needs it rather than now.
 *
 * `misses` and the median are here because they are what a padding
 * investigation would ask for, and because they are free to carry: an agent
 * clustered at eleven and a half hours after minting is a different story from
 * one clustered at four minutes, and neither is visible from a total.
 */
function Ledger({ rows }) {
  if (!rows?.length) {
    return (
      <p style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted, lineHeight: 1.6, margin: 0 }}>
        Nothing logged by anybody yet. Rows appear here as field staff log the people
        they onboard.
      </p>
    );
  }

  const cell = { padding: '9px 10px', fontFamily: SANS, fontSize: 12.5, color: O.ink };
  const head = {
    ...cell, fontSize: 11, fontWeight: 700, color: O.inkMuted,
    textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap',
  };
  const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    /* SCROLLS INSIDE ITSELF. Seven columns on a phone would otherwise push the
       whole portal sideways. */
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${O.hairline}` }}>
            <th style={{ ...head, textAlign: 'left' }}>Agent</th>
            <th style={{ ...head, textAlign: 'right' }}>Logged</th>
            <th style={{ ...head, textAlign: 'right' }}>Counted</th>
            <th style={{ ...head, textAlign: 'right' }}>Waiting</th>
            <th style={{ ...head, textAlign: 'right' }}>Tries</th>
            <th style={{ ...head, textAlign: 'right' }}>Missed</th>
            <th style={{ ...head, textAlign: 'right' }}>Median min</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.agent_oid} style={{ borderBottom: `1px solid ${O.hairline}` }}>
              <td style={{ ...cell, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.agent_oid}</td>
              <td style={num}>{r.logged}</td>
              <td style={{ ...num, color: O.green, fontWeight: 700 }}>{r.counted}</td>
              <td style={num}>{r.awaiting}</td>
              <td style={num}>{r.attempts}</td>
              {/* A high miss count is not proof of anything and is not coloured
                  as though it were. It is the number somebody would want to
                  ask about, which is a different thing from an accusation. */}
              <td style={num}>{r.misses}</td>
              <td style={num}>{r.median_minutes ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * WHAT EACH WEEK WAS WORTH.
 *
 *   "we need to include this points system on the Portal so that we can track
 *    and be able to compensate accordingly."
 *
 * A ROW PER AGENT PER WEEK, not a running total. The Letter of Engagement pays
 * by the week and the bonus is decided inside one -- a total across four weeks
 * would say what somebody did and hide what they were owed for it, which is the
 * one thing this table exists to show.
 *
 * NOTHING HERE IS ARITHMETIC. Points, target, met-or-not and the amount all
 * arrive computed, each week read against the terms in force on ITS Monday.
 * Doing the multiplication here would put the letter's own numbers in two
 * places, and the second one would be wrong the day the first changed.
 *
 * THE AMOUNT IS WHAT THE TERMS SAY, NOT WHAT WAS PAID. Orchid records; the
 * paying happens in a bank, by a person, exactly as rent does.
 */
function PayLedger({ rows }) {
  if (!rows?.length) {
    return (
      <p style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted, lineHeight: 1.6, margin: 0 }}>
        No week has anything in it yet. A week appears once somebody an agent logged has
        handed in what Orchid asks for.
      </p>
    );
  }

  const cell = { padding: '9px 10px', fontFamily: SANS, fontSize: 12.5, color: O.ink };
  const head = {
    ...cell, fontSize: 11, fontWeight: 700, color: O.inkMuted,
    textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap',
  };
  const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 560 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${O.hairline}` }}>
            <th style={{ ...head, textAlign: 'left' }}>Agent</th>
            <th style={{ ...head, textAlign: 'left' }}>Week from</th>
            <th style={{ ...head, textAlign: 'right' }}>Providers</th>
            <th style={{ ...head, textAlign: 'right' }}>Profiles</th>
            <th style={{ ...head, textAlign: 'right' }}>Points</th>
            <th style={{ ...head, textAlign: 'right' }}>Target</th>
            <th style={{ ...head, textAlign: 'right' }}>Reversed</th>
            <th style={{ ...head, textAlign: 'right' }}>Week is worth</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.agent_oid}-${r.week_start}`}
                style={{ borderBottom: `1px solid ${O.hairline}` }}>
              <td style={{ ...cell, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.agent_oid}</td>
              <td style={{ ...cell, whiteSpace: 'nowrap' }}>{r.week_start}</td>
              <td style={num}>{r.providers}</td>
              <td style={num}>{r.profiles}</td>
              {/* THE TARGET BEING MET IS A FACT ABOUT THE WEEK, so it is shown
                  on the number that decides it rather than as a badge somewhere
                  else. Green marks the met one; nothing marks the unmet one,
                  because missing the number costs the bonus and is not a fault
                  -- the letter says so in as many words. */}
              <td style={{ ...num, fontWeight: 700,
                           color: r.target_met ? O.green : O.ink }}>{r.points}</td>
              <td style={num}>{r.weekly_target}</td>
              {/* Not coloured. A reversal is a correction, not an accusation. */}
              <td style={num}>{r.reversed}</td>
              <td style={{ ...num, fontWeight: 700 }}>{randsFromCents(r.payable_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FieldOnboarding() {
  const [path, setPath] = useState('guided');
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  // SERVICE PROFILE BY DEFAULT, because it is the only choice that costs
  // nothing to get wrong -- it converts later, and the others do not come back.
  const [room, setRoom] = useState('service');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [result, setResult] = useState(null);     // { ok, message }
  const [tally, setTally] = useState(null);
  const [invitations, setInvitations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState(null);     // null = not for this person
  const [pay, setPay] = useState(null);           // null = not for this person
  const box = useRef(null);

  const load = useCallback(async () => {
    const [t, i, l, p] = await Promise.all([
      fetchMyOnboardingTally(), fetchMyInvitations(), fetchOnboardingLedger(),
      fetchOnboardingPayLedger(),
    ]);
    setTally(t.data || null);
    setInvitations(i.data || null);
    setLedger(l.forbidden ? null : (l.data || []));
    // SAME PERMISSION, ASKED SEPARATELY. Both reads are gated on
    // view_field_onboarding_ledger, so in practice they refuse together --
    // but deriving one from the other would make this screen the authority
    // on a permission the database owns, and that is the drift this portal
    // avoids everywhere else by asking the server what to render.
    setPay(p.forbidden ? null : (p.data || []));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // SWITCHING PATHS CLEARS THE ANSWER, and it has to. "Somebody has already
  // logged this one" left standing over an email field is a sentence about a
  // different person and a different action, and the agent would read it as
  // the answer to what they are about to do.
  const choosePath = (next) => {
    if (next === path) return;
    setPath(next);
    setProblem(null);
    setResult(null);
    box.current?.focus?.();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const assisted = path === 'assisted';
    const value = assisted ? email : code;
    const shapeProblem = assisted ? emailProblem(value) : connectCodeProblem(value);
    if (shapeProblem) { setProblem(shapeProblem); setResult(null); return; }

    setBusy(true);
    setProblem(null);
    const { data, error } = assisted
      ? await inviteAMemberIOnboarded(value, room)
      : await logFieldOnboarding(value, 'guided');
    setBusy(false);

    if (error) { setProblem(error); setResult(null); return; }

    // BOTH HALVES, ON BOTH PATHS. `recorded: false` and `invited: false` with a
    // message are the normal answers for a code outside the window, a claim a
    // colleague already holds, or an agent's own details — all successful calls
    // carrying a refusal, and a screen reading only `error` would show nothing.
    //
    // Normalised to one flag here, so the rendering below cannot read the wrong
    // one and quietly show every refusal as a success.
    const ok = Boolean(assisted ? data?.invited : data?.recorded);
    setResult(data ? { ok, message: data.message } : null);

    if (ok) {
      // Cleared and refocused, because the next person is already waiting.
      if (assisted) setEmail(''); else setCode('');
      load();
    }
    box.current?.focus?.();
  };

  const assisted = path === 'assisted';

  return (
    /* minmax(0, 1fr) RATHER THAN THE IMPLICIT `auto` TRACK. An auto track is
       sized to its widest item, so one oversized child -- the ledger's table --
       widens the column and every sibling with it. Capping the track at the
       container's width is what makes the table's own horizontal scroll the
       thing that gives, instead of the page. */
    <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <div>
        {/* THE CHOICE, BEFORE THE FIELD. Two plain buttons rather than one
            field that sniffs for an @: a code claims an identity that already
            exists and an address sends a stranger an email, and a screen that
            guesses between them will guess wrong while somebody waits. */}
        <div
          role="group"
          aria-label="How you onboarded them"
          style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}
        >
          {[
            ['guided', 'They signed up on their phone'],
            ['assisted', 'I am signing them up here'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={path === key}
              onClick={() => choosePath(key)}
              style={{
                fontFamily: SANS, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                padding: '9px 15px', borderRadius: 999,
                border: `1px solid ${path === key ? O.green : O.hairline}`,
                background: path === key ? O.green : 'transparent',
                color: path === key ? '#fff' : O.inkMuted,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit}>
          {/* WHICH ROOM, AND ONLY ON THE ASSISTED PATH.
              A guided signup already chose its own room on the phone the
              person is holding; asking again here would be a second answer to
              a settled question, and the two could disagree. */}
          {assisted && (
            <fieldset style={{ border: 0, padding: 0, margin: '0 0 18px' }}>
              <legend style={{
                fontFamily: SANS, fontSize: 12, fontWeight: 700, color: O.ink,
                padding: 0, marginBottom: 8,
              }}>
                What are they signing up as?
              </legend>
              <div style={{ display: 'grid', gap: 8 }}>
                {ROOMS.map((r) => (
                  <label
                    key={r.key}
                    style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
                      padding: '11px 13px', borderRadius: 12,
                      border: `1px solid ${room === r.key ? O.green : O.hairline}`,
                      background: room === r.key ? 'rgba(13,61,42,0.04)' : 'transparent',
                    }}
                  >
                    <input
                      type="radio"
                      name="room"
                      value={r.key}
                      checked={room === r.key}
                      onChange={() => { setRoom(r.key); setResult(null); }}
                      style={{ marginTop: 3, accentColor: O.green }}
                    />
                    <span>
                      <span style={{
                        display: 'block', fontFamily: SANS, fontSize: 13,
                        fontWeight: 700, color: O.ink,
                      }}>{r.label}</span>
                      <span style={{
                        display: 'block', fontFamily: SANS, fontSize: 11.5,
                        color: O.inkMuted, lineHeight: 1.6, marginTop: 2,
                      }}>{r.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
              {/* THE ONE THING AN AGENT NEEDS TO KNOW ABOUT GETTING IT WRONG.
                  A Service Profile can become anything later; the other way
                  round there is no door back. Said plainly, once, rather than
                  as a warning colour after the fact. */}
              <p style={{
                fontFamily: SANS, fontSize: 11.5, color: O.inkFaint,
                lineHeight: 1.65, margin: '10px 0 0',
              }}>
                If you are not sure, choose Service Profile. It can become anything later.
                A provider cannot go back to being one.
              </p>
            </fieldset>
          )}

          {assisted ? (
            <Field
              label="Their email address"
              htmlFor="member-email"
              hint="Orchid emails them a link to choose a password. Check it with them before you send."
              error={problem}
            >
              <input
                id="member-email"
                ref={box}
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setProblem(null); }}
                onBlur={() => setEmail((v) => v.trim().toLowerCase())}
                placeholder="name@example.com"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                style={textInputStyle(Boolean(problem))}
              />
            </Field>
          ) : (
            <Field
              label="Their OFX Code"
              htmlFor="ofx-code"
              hint="From the screen they were shown after signing up. Within 12 hours of them signing up."
              error={problem}
            >
              <input
                id="ofx-code"
                ref={box}
                value={code}
                onChange={(e) => { setCode(e.target.value); setProblem(null); }}
                onBlur={() => setCode((c) => (c ? tidyConnectCode(c) : c))}
                placeholder="OFX-XXXXXXXXX"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                style={{ ...textInputStyle(Boolean(problem)), letterSpacing: '0.06em' }}
              />
            </Field>
          )}

          {result && (
            <div style={{ marginTop: 12 }}>
              {/* THE DATABASE'S SENTENCE, VERBATIM. Every refusal it can give
                  is already written for a person to read, and rewording them
                  here would be two copies of the same message drifting apart. */}
              <Notice tone={result.ok ? 'info' : 'warning'}>{result.message}</Notice>
            </div>
          )}

          <div style={{ marginTop: 12, maxWidth: 260 }}>
            <PrimaryCTA type="submit" busy={busy} disabled={busy}>
              {/* eslint-disable-next-line no-nested-ternary */}
              {busy
                ? (assisted ? 'Sending…' : 'Logging…')
                : (assisted ? 'Send their invitation' : 'Log this onboarding')}
            </PrimaryCTA>
          </div>
        </form>

        <p style={{
          fontFamily: SANS, fontSize: 11.5, color: O.inkFaint, lineHeight: 1.7, margin: '14px 0 0',
        }}>
          {assisted
            ? 'Orchid says the same thing whichever address you send to — it will not tell you '
              + 'whether somebody is already a member, because that would make this a way to '
              + 'look up anybody. It counts once they choose a password and open Orchid, so '
              + 'check the address with them before you send it.'
            : 'Orchid does not show you anything about the person behind a code — you were there, '
              + 'and a tool that named them would be a directory of members. A code minted more '
              + 'than 12 hours ago cannot be logged, so log them while you are still with them.'}
        </p>
      </div>

      <Card style={{ background: O.paper }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
          <h4 style={{ fontFamily: SANS, fontSize: 13, fontWeight: 700, color: O.ink, margin: 0 }}>
            Your onboardings
          </h4>
          {loading && <Spinner size={13} />}
        </div>
        <MyTally tally={tally} invitations={invitations} busy={loading} />
      </Card>

      {/* HUMAN CAPITAL ONLY, and decided by the database rather than here. */}
      {ledger !== null && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
            <h4 style={{ fontFamily: SANS, fontSize: 13, fontWeight: 700, color: O.ink, margin: 0 }}>
              Everybody in the field
            </h4>
            <StatusPill tone="quiet">Human Capital</StatusPill>
          </div>
          <p style={{
            fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, lineHeight: 1.65, margin: '0 0 14px',
          }}>
            Counted is what an agent has earned: somebody they logged who has opened Orchid
            since. Tries and misses are what a padding question would ask for, and neither
            is an accusation on its own.
          </p>
          <Ledger rows={ledger} />
        </Card>
      )}

      {/* WHAT A WEEK WAS WORTH.
          This paragraph used to read "nothing here is computed into money —
          that is payroll, and payroll is not this platform", and the table
          below it did not exist. The founder ruled the other way: "we need to
          include this points system on the Portal so that we can track and be
          able to compensate accordingly." The sentence is changed rather than
          left standing over a table that contradicts it. */}
      {pay !== null && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
            <h4 style={{ fontFamily: SANS, fontSize: 13, fontWeight: 700, color: O.ink, margin: 0 }}>
              What each week was worth
            </h4>
            <StatusPill tone="quiet">Human Capital</StatusPill>
          </div>
          <p style={{
            fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, lineHeight: 1.65, margin: '0 0 14px',
          }}>
            A sign-up counts in the week the person actually handed in what Orchid asks for —
            lodged, not approved, so nobody is paid or unpaid on how fast a reviewer works.
            Each week is priced against the terms in force that week, so changing the target
            later cannot change what an earlier week was worth. Orchid records the amount;
            the paying happens in a bank.
          </p>
          <PayLedger rows={pay} />
        </Card>
      )}
    </div>
  );
}
